import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { useSessionStore } from '@/stores/SessionStore'
import { useUserStore } from '@/stores/UserStore'
import { useCachedFetch } from '@/composables/useFetch'
import { useUnitConverter } from '@/composables/useUnitConverter'
import { useDateHelper } from '@/composables/useDateHelper'
import { useUpdateKey } from '@/composables/useUpdateKey'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
import useSettingsGlobals from '@/composables/useSettingsGlobals'
import useSpotGlobals from '@/composables/useSpotGlobals'

const { $BASEURL } = useConfigurationGlobals();
const { $WEB_VIEW, $PLATFORM } = useSettingsGlobals();
const spotGlobals = useSpotGlobals();

const spotStore = () => {
  const sessionStore = useSessionStore();
  const userStore = useUserStore();

  const unitConverter = useUnitConverter();
  const dateHelper = useDateHelper();
  const updateKey = useUpdateKey();

  const apiDataFieldNames = {
    windAvg: 'avg',
    windAvgRounded: 'avg',
    windlull: 'lull',
    windGust: 'gust',
    windDir: 'dir',
    windDirText: 'dir_text',
    airTemp: 'atemp',
    airPressure: 'pres',
    surfaceTemp: 'surface_temp',
    waterSkinTemp: 'water_skin_temp',
    relativeHumidity: 'humidity',
    stationElevation: 'station_elevation',
    waterTemp: 'wtemp',
    waveHeight: 'wave_height',
    wavePeriod: 'wave_period',
    waterLevel: 'water_level'
  }
  
  const spots = ref(null);
  const spot = ref(null);
  const spotId = ref(null);
  const spotFavoriteId = ref(null);
  const spotName = ref(null);
  const spotProducts = ref(null);
  const spotRecordDetails = ref(null)
  const defaultStation = ref(null);
  const forecastModelData = ref(null);

  const spotMessage = ref(null);
  
  const iconColor = ref(null);
  const iconType = ref(null);
  
  const lat = ref(null);
  const lon = ref(null);
  
  const currentTimeLocal = ref(null);
  const currentTimeUTC = ref(null);
  const localTimezone = ref(null);
  const localTimezoneShort = ref(null);
  const localUTCOffset = ref(null);
  
  const upgradeAvailable = ref(true);
  const modelUpgradeAvailable = ref(false);

  const ncWindDataEnabled = ref(
    Object.prototype.hasOwnProperty.call(spotGlobals, '$NC_WIND_DATA_ENABLED')
      ? spotGlobals['$NC_WIND_DATA_ENABLED']
      : true
  )

  const lastAPICallFunction = ref(null);

  const status = computed(() => defaultStation.value?.status_id);
  const sourceMessage = computed(() => defaultStation.value?.source_message)
  const statusMessage = computed(() => defaultStation.value?.status_message)
  const forecastModels = computed(() => forecastModelData.value.model_summary)
  const isArbitrarySpot = computed(() => !spot.value.fav_spot_id && !spot.value?.spot_id && spot.value?.lat !== null && spot.value?.lon !== null);
  const isNowcast = computed(() => defaultStation.value?.type === 100 || defaultStation.value?.type === 101);
  const isPro = computed(() => defaultStation.value?.provider === 1 || defaultStation.value?.provider === 1370);
  const isNCWind = computed(() => defaultStation.value?.is_nc_wind);
  const isNCWindCapable = computed(() => defaultStation.value?.is_nc_wind_capable)
  const isMultiStationSpot = computed(() => spot.value?.stations && spot.value.stations.length > 1);
  const proForecastIdIfExists = computed(() => spot.value?.op_forecast_id_if_spot_forecast_exists || null);

  const hasSpotProduct = computed(() => (spotProductId) => spotProducts.value?.includes(spotProductId));

  const isSpotFavorited = computed(() => {
    let spotFavorited = null

    if (!userStore.userProfiles.profiles) return spotFavorited
    if (userStore.userProfiles.profiles.length === 0) return spotFavorited

    for (let profile of userStore.userProfiles.profiles) {
      spotFavorited = isSpotFavoritedInProfile.value(profile.profile_id)

      if (spotFavorited) break
    }

    return spotFavorited
  })

  const isSpotFavoritedInProfile = computed(() => {
    return (profileId) => {
      const profile = userStore.getProfileById(profileId)
      let spotFavorited = null

      for (let favoriteSpot of profile.favorite_spots) {
        if (
          !favoriteSpot.spot_id &&
          favoriteSpot.lat === lat.value &&
          favoriteSpot.lon === lon.value
        ) {
          spotFavorited = [profileId, favoriteSpot.fav_spot_id]
          break
        }

        if (!spot.value) continue;

        if (
          favoriteSpot.spot_id &&
          spot.value.spot_id &&
          favoriteSpot.spot_id === spot.value.spot_id
        ) {
          spotFavorited = [profileId, favoriteSpot.fav_spot_id]
          break
        }
      }

      return spotFavorited
    }
  });

  const isContainerFavoritedInProfile = computed(() => {
    return (profileId) => {
      const profile = userStore.getProfileById(profileId)
      let containerFavorited = null

      for (let container of profile.containers) {

        if (container.container_id) {
          containerFavorited = [profileId, container.container_id]
          break
        }
      }

      return containerFavorited;
    }
  });

  const stationCapabilities = computed(() => {
    if (!spot.value || !spot.value.stations) return [];

    const stationCapabilites = [];

    spot.value.stations.forEach(station => {
      if (!station.capabilities) return;

      station.capabilities.forEach(capability => {
        if (
          stationCapabilites.indexOf(capability) === -1 &&
          Object.prototype.hasOwnProperty.call(
            spotGlobals.$ALLOWED_STATION_CAPABILITIES,
            capability
          )
        ) {
          stationCapabilites.push(spotGlobals.$ALLOWED_STATION_CAPABILITIES[capability])
        }
      })
    })

    return stationCapabilites;
  })

  const stationApiDataValues = computed(() => {
    if (!spot.value || !spot.value.stations) return {};

    const apiDataValues = defaultStation.value.data_values[0];
    const dataValues = {};
    
    if (!apiDataValues) return dataValues;

    spotRecordDetails.value.forEach((spotRecordDetail, i) => {
      const dataValue = scrubData(apiDataValues[i]);

      dataValues[spotRecordDetail] = dataValue;
    });

    return dataValues;
  });

  const formattedData = computed(() => ({
    windAvg: formatData(stationApiDataValues.value?.avg, userStore.units.wind),
    windAvgRounded: formatData(stationApiDataValues.value?.avg, userStore.units.wind, { round: true }),
    windlull: formatData(stationApiDataValues.value?.lull, userStore.units.wind),
    windGust: formatData(stationApiDataValues.value?.gust, userStore.units.wind),
    windDir: formatData(stationApiDataValues.value?.dir, '&deg;', { useSpacer: false }),
    windDirText: formatData(stationApiDataValues.value?.dir_text),
    airTemp: formatData(stationApiDataValues.value?.atemp, `&deg;${userStore.units.temp}`),
    airPressure: formatAirPressure.value(stationApiDataValues.value?.pres),
    surfaceTemp: formatData(stationApiDataValues.value?.surface_temp, `&deg;${userStore.units.temp}`),
    waterSkinTemp: formatData(stationApiDataValues.value?.water_skin_temp, `&deg;${userStore.units.temp}`),
    relativeHumidity: formatData(stationApiDataValues.value?.humidity, '%', { useSpacer: false }),
    stationElevation: processStationElevationData(stationApiDataValues.value?.station_elevation),
    waterTemp: formatData(stationApiDataValues.value?.wtemp, `&deg;${userStore.units.temp}`),
    waveHeight: formatData(stationApiDataValues.value?.wave_height, 'ft'),
    wavePeriod: formatData(stationApiDataValues.value?.wave_period, 'ft'),
    waterLevel: formatData(stationApiDataValues.value?.water_level, 'ft')
  })); 

  const formatAirPressure = computed(() => {
    return (value) => {
      if (!value) return formatData(stationApiDataValues.value?.pres, 'mb')

      return value < 500
        ? formatData(stationApiDataValues.value?.pres, 'inHg')
        : formatData(stationApiDataValues.value?.pres, 'mb')
    }
  }) 

  const spotApiData = computed(() => {
    if (!spot.value || !spot.value.stations) return {}

    return {
      spotId: spotId.value,
      spotFavoriteId: spotFavoriteId.value,
      spotName: spotName.value,
      forecastModelData: forecastModelData.value,
      isMultiStationSpot: isMultiStationSpot.value,
      lat: lat.value,
      lon: lon.value,
      localUTCOffset: localUTCOffset.value,
      upgradeAvailable: upgradeAvailable.value,
      stationCapabilities: stationCapabilities.value,
      status: status.value,
      isNowcast: isNowcast.value,
      iconColor: iconColor.value,
      iconType: iconType.value,
      stationApiDataValues: stationApiDataValues.value,
      formattedData: formattedData.value,
      station: defaultStation.value,
      spotProducts: spotProducts.value,
      proForecastIdIfExists: proForecastIdIfExists.value
    }
  })     

  async function getSpotDetailById(spotId, settings = {}, overrideCache = false) {
    getSavedNCWindSettings(spotId);

    const data = await getSpotDetailSetByList([spotId], false, settings, overrideCache);

    lastAPICallFunction.value = [getSpotDetailById, [spotId], false, settings, overrideCache];

    if (data.error) {
      const message = !spotId ? 'Spot id was not provided.' : data.message;

      data.errorObject = {
        header: `Sorry, there was a problem.`,
        title: `The complete report could not be loaded for this spot id.`,
        message
      }         

      return data
    }

    updateSpotData(data);

    await getAvailableModelDataBySpotId(spotId);

    return data;
  }

  async function getSpotDetailByFavoriteId(favSpotId, settings = {}) {
    getSavedNCWindSettings(favSpotId);

    const data = await getSpotDetailSetByList([favSpotId], true, settings);

    lastAPICallFunction.value = [getSpotDetailSetByList, [favSpotId], true, settings];

    if (data.error) {
      const message = !favSpotId ? 'Spot id was not provided.' : data.message

      data.errorObject = {
        header: `Sorry, there was a problem.`,
        title: `The complete report could not be loaded for this spot id.`,
        message
      }      

      return data
    }

    updateSpotData(data)

    await getAvailableModelDataByFavoriteSpotId(favSpotId);

    return data;
  }

  async function getSpotDetailByLatLon(latLon, settings = {}) {
    const data = await getSpotDetailSetByList([latLon], false, settings)

    lastAPICallFunction.value = [getSpotDetailSetByList, [latLon], true, settings]

    if (data.error) {
      const message = !latLon ? 'Lat/lon was not provided.' : data.message

      data.errorObject = {
        header: `Sorry, there was a problem.`,
        title: `The complete report could not be loaded for this spot.`,
        message
      }         

      return data;
    }

    updateSpotData(data);

    const [lat, lon] = latLon.split(':');

    await getAvailableModelDataByLatLon(lat, lon);

    return data
  }  

  async function getSpotDetailSetByList(spotList, favorite = false, settings = {}, overrideCache = false) {
    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      include_spot_products: true,
      stormprint_only: false,
      wf_token: sessionStore.token
    }

    if (!ncWindDataEnabled.value) {
      defaultSettings.nc_wind = false;
    }

    const params = Object.assign(defaultSettings, settings)

    if (favorite) {
      params.fav_spot_list = spotList.join(',')
    } else {
      params.spot_list = spotList.join(',')
    }

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/spot/getSpotDetailSetByList?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url, {}, true, overrideCache)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      if (data.value.spot_count === 0) {
        throw 'No spots found'
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error, url }
    }
  }

  async function getSpotDetailSetBySearch(search, settings = {}, overrideCache = false) {
    if (!search && !spotName.value) return;

    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      spot_types: 1,
      num_per_page: 30,
      search_dist: 10,
      sort: 'distance',
      stormprint_only: false,
      search: search || spotName.value,
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/spot/getSpotDetailSetBySearch?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url, {}, true, overrideCache)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error, url }
    }
  }

  async function getAvailableModelDataBySpotId(spotId, settings = {}) {
    const data = await getAvailableModelsBySpot(spotId, false, settings);

    if (data.error) return data;

    updateModelData(data);

    return data;
  }

  async function getAvailableModelDataByFavoriteSpotId(favSpotId, settings = {}) {
    const data = await getAvailableModelsBySpot(favSpotId, true, settings);

    if (data.error) return data;

    updateModelData(data);

    return data    
  }

  async function getAvailableModelDataByLatLon(lat, lon, settings = {}) {
    const data = await getAvailableModelsByLatLon(lat, lon, settings)

    if (data.error) return data;

    updateModelData(data);

    return data    
  }

  async function getNearbySpots(search, settings, overrideCache = false) {
    if (!search && !spotName.value) return

    const searchTerm = search || spotName.value

    const data = await getSpotDetailSetBySearch(searchTerm, settings, overrideCache)

    if (data.error) return data

    return data
  }

  async function getAvailableModelsBySpot(id, favorite = false, settings = {}) {
    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    if (favorite) {
      params.fav_spot_id = id;
    } else {
      params.spot_id = id;
    }

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/model/getAvailableModelsBySpot?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url);

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  } 

  async function getAvailableModelsByLatLon(lat, lon, settings = {}) {
    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    params.lat = lat;
    params.lon = lon;

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/model/getAvailableModelsByLatLon?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url);

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  }

  function getSavedNCWindSettings(id = spotId.value) {
    if ($WEB_VIEW) return ncWindDataEnabled.value;

    const ncWindLocalStorage = localStorage.getItem('ncWindDisabled') || '[]';

    updateNCWindDataEnabled(!ncWindLocalStorage.includes(id), id, false);
  }  

  function setSavedNCWindSettings(enabled, id = spotId.value) {
    let ncWindLocalStorage = JSON.parse(localStorage.getItem('ncWindDisabled')) || []

    if (!enabled) {
      ncWindLocalStorage.push(id)
    } else {
      ncWindLocalStorage = ncWindLocalStorage.filter((disabledNCWindSpotId) => disabledNCWindSpotId !== id)
    }

    localStorage.setItem('ncWindDisabled', JSON.stringify(ncWindLocalStorage));
  }  

  function setSavedNCWindSettingsWebView(value) {
    if (!$WEB_VIEW) return;

    if ($PLATFORM === 'android') {
      const params = ['nc_wind_data_enabled', value]

      alert(params.join(','))
    } else {
      const info = {
        type: 'nc_wind_data_enabled',
        value: value
      }

      window.webkit.messageHandlers.observe.postMessage(info)
    }
  }  

  async function updateNCWindDataEnabled(isEnabled, id = spotId.value, saveToLocalStorage = true) {
    if (isEnabled === ncWindDataEnabled.value) return;
    
    ncWindDataEnabled.value = isEnabled;    
    if (!saveToLocalStorage) return;

    if ($WEB_VIEW) {
      setSavedNCWindSettingsWebView(isEnabled);
    } else {
      setSavedNCWindSettings(isEnabled, id);
    }

    const calledMade = makeLastAPICall();

    if (!calledMade) {
      await getSpotDetailById(id, {}, true);
    }
  }

  async function setCurrentSpotWithData(data) {
    const id = data.spot_id || null;
    lastAPICallFunction.value = null;

    if (id) {
      getSavedNCWindSettings(id);
      const dataIsNCWind = data.stations[0]?.is_nc_wind

      // Check to make sure passed in spot data matches ncWindDataEnabled saved setting or else refetch
      if (dataIsNCWind && dataIsNCWind !== ncWindDataEnabled.value) {
        await getSpotDetailById(id, {}, true);

        return;
      }
    };

    updateSpotData(data);

    if (id) await getAvailableModelDataBySpotId(id);
  }

  function updateSpotData(data) {
    spots.value = data.spots ? data.spots : null;

    const currentSpot = spots.value && spots.value.length ? spots.value[0] : data;

    spot.value = currentSpot;
    spotId.value = currentSpot.spot_id;
    spotFavoriteId.value = currentSpot.fav_spot_id
    spotName.value = currentSpot.name;
    spotRecordDetails.value = currentSpot.data_names
    spotProducts.value = currentSpot.spot_product_ids;
    spotMessage.value = currentSpot.spot_message;

    lat.value = currentSpot.lat;
    lon.value = currentSpot.lon;

    currentTimeLocal.value = data.current_time_local || currentSpot.current_time_local
    currentTimeUTC.value = data.current_time_utc || currentSpot.current_time_utc
    localTimezone.value = data.local_timezone || currentSpot.local_timezone || currentSpot.tz_name
    localTimezoneShort.value = data.local_timezone_short || currentSpot.local_timezone_short || currentSpot.tz_name_short
    localUTCOffset.value = data.local_utc_offset || currentSpot.local_utc_offset || currentSpot.tz_offset

    if (localTimezone.value && /[-+]/.test(localTimezone.value)) {
      localTimezone.value = localTimezone.value.split(/[-+]/)[0]
    }

    if (localTimezoneShort.value && /[-+]/.test(localTimezoneShort.value)) {
      localTimezoneShort.value = localTimezoneShort.value.split(/[-+]/)[0]
    }

    if (typeof localUTCOffset.value === 'number') localUTCOffset.value = dateHelper.formatOffsetToString(localUTCOffset.value)

    iconColor.value = currentSpot.icon_color;
    iconType.value = currentSpot.icon_type;

    upgradeAvailable.value = currentSpot.upgrade_available;

    getDefaultStation(currentSpot.stations);
  }

  function getDefaultStation(stations) {
    if (stations.length === 0) return;

    defaultStation.value = stations[0];

    // Multi-station spot. Use station with lowest elevation as default.
    if (stations.length > 1) {
      for (let i = 1; i < stations.length; i++) {
        if ( stations[i].station_elevation && defaultStation.value.station_elevation > stations[i].station_elevation ) {
          defaultStation.value = stations[i];
        }
      }
    }
  }

  function updateModelData(data) {
    forecastModelData.value = data;

    isModelUpgradeAvailable(data.model_summary);
  }

  function isModelUpgradeAvailable(modelData) {
    for (let model of modelData) {
      if (model.upgrade_available) {
        modelUpgradeAvailable.value = true;
        break;
      }
    }
  }

  function scrubData(value) {
    return (
      typeof value === undefined ||
      value === null ||
      value === 'null' ||
      value === '' ||
      value === 'undefined' ||
      value === undefined
    ) ? null : value
  }

  function formatData(data, unit, settings = {}) {
    if (data === null || status.value === 4) return '<span class="jwx-data-value__value">&ndash;&ndash;</span>'

    const defaultSettings = {
      useSpacer: false,
      spacer: '',
      round: false
    }

    settings = Object.assign({}, defaultSettings, settings)

    const spacer = settings.useSpacer ? settings.spacer : ''

    if (settings.round) data = Math.round(data)

    return unit
      ? `<span class="jwx-data-value__value">${data}</span>${spacer}<span class="jwx-data-value__unit">${unit}</span>`
      : `<span class="jwx-data-value__value">${data}</span>`
  }

  function processStationElevationData(value) {
    if (userStore.units.distance === 'ft') {
      return formatData(value, 'userStore.units.distance')
    }

    const convertedValue = unitConverter.convert('ft', 'm', value)
    return formatData(convertedValue, 'm')
  }

  function makeLastAPICall() {
    if (!lastAPICallFunction.value) return false;

    const apiCallfunction = lastAPICallFunction.value[0]
    const args = lastAPICallFunction.value.slice(1)

    apiCallfunction(...args);

    return true;
  }

  async function refreshSpotData() {
    if (spotId.value) {
      await getSpotDetailById(spotId.value, {}, true)
    }
  }

  watch(updateKey.key, async () => {
    await refreshSpotData();
  })

  return {
    apiDataFieldNames,
    currentTimeLocal,
    currentTimeUTC,
    defaultStation,
    forecastModelData,
    forecastModels,
    formattedData,
    hasSpotProduct,
    iconColor,
    iconType,
    isArbitrarySpot,
    isContainerFavoritedInProfile,
    isMultiStationSpot,
    isNCWind,
    isNCWindCapable,
    isNowcast,
    isPro,
    isSpotFavorited,
    isSpotFavoritedInProfile,
    lat,
    localTimezone,
    localTimezoneShort,
    localUTCOffset,
    lon,
    modelUpgradeAvailable,
    ncWindDataEnabled,
    proForecastIdIfExists,
    sourceMessage,
    spot,
    spotApiData,
    spotFavoriteId,
    spotId,
    spotMessage,
    spotName,
    spotProducts,
    spots,
    stationApiDataValues,
    stationCapabilities,
    status,
    statusMessage,
    upgradeAvailable,
    getAvailableModelDataByFavoriteSpotId,
    getAvailableModelDataByLatLon,
    getAvailableModelDataBySpotId,
    getAvailableModelsBySpot,
    getSavedNCWindSettings,
    getSpotDetailByFavoriteId,
    getSpotDetailById,
    getSpotDetailByLatLon,
    getSpotDetailSetByList,
    getSpotDetailSetBySearch,
    getNearbySpots,
    setCurrentSpotWithData,
    updateNCWindDataEnabled
  }
}

export const useSpotStore = (storeId) => {
  const storeName = storeId ? `spotStore-${storeId}` : 'spotStore';
  
  const store = defineStore(storeName, spotStore);

  return store();
}
